import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Environment, TimePrecision } from '@playq/octopus-common';
import { AppIssue, AppSupportRequest } from '@playq/services-beetle';
import { GenericFailure } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery } from '/api/service-hooks';
import { appToolkit } from '/store';
import { toMs } from '/helpers/toMs';

import { IBeetleId } from './types';
import { addNote, removeNote, updateNote } from './notesCache';
import { methods } from './serviceHandlerRequest';
import { audienceRetrieveQueryKeys } from './constants';
import { SupportContentType } from './types/supportContentType';

export const useRetrieve = (
  id: IBeetleId,
  type: SupportContentType,
  env: Environment = Environment.Live,
  options?: IUseQueryOptionsExtended<AppIssue | AppSupportRequest | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);
  const keys = [...audienceRetrieveQueryKeys, id, env, appID];
  const service = methods(type);
  const defaultEntity =
    type === SupportContentType.ISSUES || type === SupportContentType.ISSUES_ATTACHMENTS
      ? new AppIssue()
      : new AppSupportRequest();
  const res = useEitherQuery(keys, () => service.retrieve(id, env), {
    placeholderData: keepPreviousData,
    refetchInterval: toMs(TimePrecision.Minute, 14),
    refetchIntervalInBackground: true,
    staleTime: toMs(TimePrecision.Minute, 14),
    gcTime: toMs(TimePrecision.Minute, 1),
    ...options,
  });
  const createPrevDataClass = useCallback((oldData: AppIssue | AppSupportRequest): AppIssue | AppSupportRequest => {
    if (oldData instanceof AppIssue) {
      return new AppIssue(oldData.serialize());
    }
    if (oldData instanceof AppSupportRequest) {
      return new AppSupportRequest(oldData.serialize());
    }
    throw new Error('Unsupported data type');
  }, []);

  const cacheOptimization = useMemo(() => {
    return {
      addNote: addNote(createPrevDataClass, res.mutate),
      removeNote: removeNote(createPrevDataClass, res.mutate),
      updateNote: updateNote(createPrevDataClass, res.mutate),
    };
  }, [createPrevDataClass, res.mutate]);

  return {
    ...res,
    keys,
    updatedAt: res.dataUpdatedAt,
    entity: res.data || defaultEntity,
    cacheOptimization,
  };
};
